import { useQuery } from "@tanstack/react-query";
import { CalendarDays, TrendingUp, Target } from "lucide-react";
import {
  getChildWeeklySummary,
  getClassWeeklySummary,
} from "@/lib/weekly-summary.functions";

type Props =
  | { kind: "child"; learnerId: string; name?: string }
  | { kind: "class"; streamId: string; name?: string };

export function WeeklySummaryCard(props: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["weekly-summary", props.kind, props.kind === "child" ? props.learnerId : props.streamId],
    queryFn: () =>
      props.kind === "child"
        ? getChildWeeklySummary({ data: { learnerId: props.learnerId } })
        : getClassWeeklySummary({ data: { streamId: props.streamId } }),
    enabled: props.kind === "child" ? !!props.learnerId : !!props.streamId,
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <CalendarDays className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {data?.headline ?? (props.name ? `${props.name}'s week` : "This week")}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {data?.week_start ? `Week of ${data.week_start}` : "Weekly summary"}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-3 text-xs text-muted-foreground">Loading…</div>
      ) : !data || (data.activities === 0 && data.bullets.length === 0) ? (
        <div className="mt-3 text-xs text-muted-foreground">
          No activity recorded this week yet.
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat label="Activities" value={String(data.activities)} />
            <Stat
              icon={<TrendingUp className="h-3 w-3" />}
              label="Improved"
              value={truncate(data.improved_most)}
            />
            <Stat
              icon={<Target className="h-3 w-3" />}
              label="Focus"
              value={truncate(data.next_focus)}
            />
          </div>
          {data.bullets.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {data.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-muted/40 p-2">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-xs font-medium truncate">{value}</div>
    </div>
  );
}

function truncate(v: string | null | undefined) {
  if (!v) return "—";
  return v.length > 18 ? v.slice(0, 17) + "…" : v;
}
